/**
 * The owner overview's "Email history" card.
 *
 * Every entry's "sent" flag and timestamp must come from owner_lifecycle_emails
 * (20260826160000) — a real send-log written only by sendAndStampOwnerLifecycleEmail
 * after an actual successful send. It must NEVER be reconstructed from the
 * *_owner_email_sent_at stamp columns on bookings/apartments/payments: those
 * are an idempotency CLAIM (set before the muted-preference check even runs)
 * and, for anything predating the feature, a historical business-event proxy
 * backfilled by 20260826150000 — neither is evidence a mail went out, which
 * is exactly why this card used to show fabricated "Sent" entries.
 *
 * The business-event columns (contract_signed_at, deposit paid_at, etc.) are
 * still used, but only to decide whether a row should render at ALL — an
 * event that happened but has no matching log row renders as pending, not
 * hidden.
 *
 * `portal_ready` is the exception to that gating: it has no business-event
 * column at all (see 20260826150000's own note), so it renders straight from
 * the log and can never appear as pending.
 *
 * `rent_received` used to be a second exception, for a reason that no longer
 * holds: it recurs monthly, so there is no one-per-BOOKING column to gate it
 * on. But there is a one-per-MONTH event — the settled `payments` row itself
 * — and gating on that is what this card does for every other entry. While
 * rent rendered straight from the log, a month whose mail was suppressed as
 * `already_sent` (the stamp claimed by 20260826150000's backfill, or by an
 * earlier attempt) produced no log row and therefore no entry at all: an
 * admin marked September paid, the payment was genuinely recorded, and the
 * owner's profile showed nothing whatsoever. Rent now renders one entry per
 * paid rent payment, matched to its log attempts by `payment_id`, so the
 * month always appears and the badge tells the truth about the mail.
 *
 * Legacy log rows that no payment claims (written before payment_id existed
 * and unmatched by that migration's backfill, or whose payment has since been
 * deleted) still render off the log, so nothing that showed before this
 * change disappears.
 *
 * Kept as a pure function, separate from the hook that fetches the rows, so
 * the ordering/gating rules are testable without a Supabase client.
 */

/**
 * `rent_received` recurs monthly, so an owner with several occupied units
 * accumulates more of these than every other type combined — a year of them
 * would bury the one-shot lifecycle events this card exists to show. Capped to
 * the most recent N overall, matching the 12-month window useOwnerEmailHistory
 * already applies to management invoices.
 */
const RENT_RECEIVED_LIMIT = 12;

export interface OwnerEmailHistoryEntry {
  title: string;
  desc: string;
  sent: boolean;
  ts: string | null;
  /**
   * Set when the log holds a recorded FAILURE for this event rather than a
   * send (20260903140000) — an owner with no address on file, a category they
   * switched off, a mail the provider rejected.
   *
   * Distinct from `sent: false` on its own, which means "this happened and we
   * have no record either way". That distinction is the whole point: those two
   * used to render identically as "Pending", so an owner mail that had
   * definitively failed looked like one still on its way, and nobody chased
   * it. Present here implies `sent: false`.
   */
  notDeliveredReason?: string | null;
  /**
   * Who recorded the underlying event, when a person did — today only rent,
   * whose `payments.marked_paid_by` names the admin who attested an
   * off-platform settlement.
   *
   * Its own field rather than more text on `desc` because the card truncates
   * `desc` to one line: appended there, the name an owner is being shown
   * would be the first thing to disappear at narrow widths, which is the
   * opposite of the point.
   */
  attribution?: string | null;
}

export interface OwnerEmailHistoryApartment {
  id: number;
  label: string | null;
}

export interface OwnerEmailHistoryBooking {
  id: number;
  name: string | null;
  apartment_label: string | null;
  contract_signed_at: string | null;
  deposit_paid_at: string | null;
  key_picked_up_at: string | null;
  move_in_protocol_submitted_at: string | null;
  move_out_date: string | null;
  move_out_protocol_submitted_at: string | null;
  key_returned_at: string | null;
}

export interface OwnerEmailHistoryInvoice {
  invoice_no: string | null;
  period_month: string | null;
  sent_at: string | null;
}

export interface OwnerEmailHistorySettlement {
  tenant_name: string | null;
  bundle_sent_at: string | null;
  review_reminded_at: string | null;
  statement_sent_at: string | null;
  refund_paid_at: string | null;
}

/** One row per ATTEMPT from owner_lifecycle_emails — see `outcome`. */
export interface OwnerEmailHistorySentLog {
  email_type: string;
  sent_at: string;
  booking_id: number | null;
  apartment_id: number | null;
  /**
   * 'sent' | 'skipped' | 'failed' (20260903140000). Only 'sent' is evidence a
   * mail exists.
   *
   * Optional, and absent means 'sent': every row written before that migration
   * predates the column and was only ever inserted after a successful send, so
   * the DB default and this fallback agree. Treating an unknown value as sent
   * would be the dangerous direction — that is 20260826160000's fabricated-
   * "Sent" bug — so anything that is neither 'sent' nor absent counts as a
   * non-send.
   */
  outcome?: string | null;
  /** Machine reason on a non-send: no_recipient | muted | send_failed. */
  skip_reason?: string | null;
  /**
   * The settled `payments` row this attempt was about (20260904140000).
   *
   * Only rent/deposit attempts stamp `payments`, so this is null on every
   * booking- and apartment-scoped attempt. For rent it is the only exact key
   * between an attempt and the month it belongs to: `sent_at` is when the
   * attempt happened, which is not the rent period (a July rent settled in
   * September would otherwise be filed under September).
   */
  payment_id?: string | null;
}

/**
 * A settled rent payment — the per-month business event a `rent_received`
 * entry is gated on, exactly as `contract_signed_at` gates the lease entry.
 *
 * The attestation columns are what let the entry name the admin who settled
 * it: `marked_paid_by` is written by every admin mark-paid surface and is
 * deliberately absent on a Stripe settlement (see TenantProfileDialog's
 * `isStripeRow`), so its presence is the difference between "the tenant paid"
 * and "an admin recorded this as paid".
 */
export interface OwnerEmailHistoryRentPayment {
  id: string;
  booking_id: number;
  /** The rent period. Never `paid_at`, which is when money arrived. */
  due_date: string | null;
  paid_at: string | null;
  marked_paid_at: string | null;
  /**
   * `payments.marked_paid_by` — present iff an admin attested this
   * settlement. Kept alongside the name because the two answer different
   * questions: the id says an admin recorded it (a fact worth showing even
   * when their profile could not be read), the name says which one.
   */
  marked_paid_by_id: string | null;
  /** Display name (or email) of that admin, when it resolved. */
  marked_paid_by_name: string | null;
}

/** Reasons an owner mail did not go out, in the card's own voice. */
const NOT_DELIVERED_REASON: Record<string, string> = {
  no_recipient: "no email address on file for this owner",
  muted: "the owner switched this notification off",
  send_failed: "the mail provider rejected it",
};

function reasonText(skipReason: string | null | undefined): string {
  const key = String(skipReason ?? "").trim();
  return NOT_DELIVERED_REASON[key] || "not delivered";
}

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

/**
 * "September 2026" from a rent period.
 *
 * Read off the ISO string rather than through `new Date(...)`: `due_date` is a
 * date-only column, which parses as UTC midnight and would render as the
 * PREVIOUS month for any admin west of UTC — the one place a rent entry names
 * a month is not the place to be off by one.
 */
function monthLabelDe(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})/.exec(String(iso ?? ""));
  if (!m) return null;
  const month = MONTHS_DE[Number(m[2]) - 1];
  return month ? `${month} ${m[1]}` : null;
}

/** A row is send evidence only when it says so; absent = pre-migration send. */
function isSentLog(log: OwnerEmailHistorySentLog): boolean {
  return log.outcome == null || log.outcome === "sent";
}

export function buildOwnerEmailHistory(input: {
  apartments: OwnerEmailHistoryApartment[];
  bookings: OwnerEmailHistoryBooking[];
  invoices: OwnerEmailHistoryInvoice[];
  settlements: OwnerEmailHistorySettlement[];
  sentLogs: OwnerEmailHistorySentLog[];
  /**
   * Optional so a caller whose payments query degraded to nothing falls back
   * to the old log-only rent rendering rather than losing rent entirely.
   */
  rentPayments?: OwnerEmailHistoryRentPayment[];
}): OwnerEmailHistoryEntry[] {
  const out: OwnerEmailHistoryEntry[] = [];
  const push = (
    title: string,
    desc: string,
    sent: boolean,
    ts: string | null,
    notDeliveredReason?: string | null,
    attribution?: string | null
  ) => {
    const entry: OwnerEmailHistoryEntry = notDeliveredReason
      ? { title, desc, sent: false, ts, notDeliveredReason }
      : { title, desc, sent, ts };
    if (attribution) entry.attribution = attribution;
    out.push(entry);
  };

  // Latest real send per (booking_id, email_type) / per apartment_id.
  const sentByBookingType = new Map<string, string>();
  const sentByApartment = new Map<number, string>();
  /**
   * Latest recorded FAILURE per (booking_id, email_type), consulted only when
   * there is no send for the same key — a later successful retry must win, or
   * the card would report a resolved problem forever.
   */
  const failedByBookingType = new Map<
    string,
    { at: string; reason: string | null | undefined }
  >();
  for (const log of input.sentLogs) {
    if (log.apartment_id != null) {
      if (!isSentLog(log)) continue;
      const existing = sentByApartment.get(log.apartment_id);
      if (!existing || log.sent_at > existing) {
        sentByApartment.set(log.apartment_id, log.sent_at);
      }
    } else if (log.booking_id != null) {
      const key = `${log.booking_id}:${log.email_type}`;
      if (isSentLog(log)) {
        const existing = sentByBookingType.get(key);
        if (!existing || log.sent_at > existing) {
          sentByBookingType.set(key, log.sent_at);
        }
      } else {
        const existing = failedByBookingType.get(key);
        if (!existing || log.sent_at > existing.at) {
          failedByBookingType.set(key, {
            at: log.sent_at,
            reason: log.skip_reason,
          });
        }
      }
    }
  }

  /**
   * A recorded failure for this event, or null when there is none or a send
   * has since superseded it.
   */
  const failureFor = (bookingId: number, type: string) => {
    const key = `${bookingId}:${type}`;
    const sentAt = sentByBookingType.get(key);
    const failed = failedByBookingType.get(key);
    if (!failed) return null;
    if (sentAt && sentAt >= failed.at) return null;
    return failed;
  };

  for (const apt of input.apartments) {
    const sentAt = sentByApartment.get(apt.id);
    if (!sentAt) continue; // no reliable "should have been sent" proxy — see 20260826150000's own note on portal_ready
    push(
      "Owner Portal Ready",
      `Willkommen bei coliviq · ${apt.label ?? "—"}`,
      true,
      sentAt
    );
  }

  for (const b of input.bookings) {
    const who = b.name?.trim() || "Tenant";
    const unit = b.apartment_label ?? "—";
    const sentFor = (type: string) =>
      sentByBookingType.get(`${b.id}:${type}`) ?? null;

    if (b.contract_signed_at) {
      const sentAt = sentFor("lease_signed");
      const failed = sentAt ? null : failureFor(b.id, "lease_signed");
      push(
        "New lease agreement signed",
        `Mietvertrag unterschrieben · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.deposit_paid_at) {
      const sentAt = sentFor("deposit_received");
      const failed = sentAt ? null : failureFor(b.id, "deposit_received");
      push(
        "Security deposit received",
        `Kaution eingegangen · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.key_picked_up_at) {
      const sentAt = sentFor("keys_handed_over");
      const failed = sentAt ? null : failureFor(b.id, "keys_handed_over");
      push(
        "Keys handed over",
        `Schlüsselübergabe · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.move_in_protocol_submitted_at) {
      const sentAt = sentFor("move_in_protocol");
      const failed = sentAt ? null : failureFor(b.id, "move_in_protocol");
      push(
        "Move-in protocol available",
        `Übergabeprotokoll eingereicht · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.move_out_date) {
      const sentAt = sentFor("move_out_date_confirmed");
      const failed = sentAt
        ? null
        : failureFor(b.id, "move_out_date_confirmed");
      push(
        "Move-out date confirmed",
        `Auszugstermin bestätigt · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.move_out_protocol_submitted_at) {
      const sentAt = sentFor("move_out_protocol");
      const failed = sentAt ? null : failureFor(b.id, "move_out_protocol");
      push(
        "Move-out protocol available",
        `Auszugsprotokoll verfügbar · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
    if (b.key_returned_at) {
      const sentAt = sentFor("keys_returned");
      const failed = sentAt ? null : failureFor(b.id, "keys_returned");
      push(
        "Keys returned",
        `Schlüsselrückgabe · ${who} · ${unit}`,
        !!sentAt,
        sentAt ?? failed?.at ?? null,
        failed ? reasonText(failed.reason) : null
      );
    }
  }

  /**
   * Rent: one entry per settled month, gated on the `payments` row that IS
   * the month's business event (see the header). The log is consulted only
   * for what became of the mail, never for whether the month happened.
   */
  const bookingById = new Map(input.bookings.map((b) => [b.id, b]));
  const rentLogs = input.sentLogs.filter(
    (l) => l.email_type === "rent_received" && l.booking_id != null
  );
  const rentPayments = input.rentPayments ?? [];
  const rentPaymentIds = new Set(rentPayments.map((p) => p.id));

  /** Latest send and latest failure per payment — one month's attempts. */
  const rentAttempts = new Map<
    string,
    {
      sentAt: string | null;
      failed: { at: string; reason?: string | null } | null;
    }
  >();
  for (const log of rentLogs) {
    const pid = log.payment_id ?? null;
    if (!pid || !rentPaymentIds.has(pid)) continue;
    const cur = rentAttempts.get(pid) ?? { sentAt: null, failed: null };
    if (isSentLog(log)) {
      if (!cur.sentAt || log.sent_at > cur.sentAt) cur.sentAt = log.sent_at;
    } else if (!cur.failed || log.sent_at > cur.failed.at) {
      cur.failed = { at: log.sent_at, reason: log.skip_reason };
    }
    rentAttempts.set(pid, cur);
  }

  /**
   * Collected before pushing so the cap applies to rent as a whole — the
   * payment-derived entries and the legacy log-only ones below are the same
   * kind of row and must not each get their own twelve.
   */
  const rentCandidates: Array<{
    desc: string;
    sent: boolean;
    ts: string | null;
    reason: string | null;
    attribution: string | null;
  }> = [];

  for (const pay of rentPayments) {
    const b = bookingById.get(pay.booking_id);
    const who = b?.name?.trim() || "Tenant";
    const unit = b?.apartment_label ?? "—";
    const attempts = rentAttempts.get(pay.id) ?? { sentAt: null, failed: null };
    // A later successful retry supersedes an earlier failure. The reverse is
    // not symmetric: this key is one payment, so a failure logged after a send
    // is a re-attempt on a month the owner already has.
    const failed =
      attempts.sentAt && attempts.sentAt >= (attempts.failed?.at ?? "")
        ? null
        : attempts.failed;
    /**
     * The event's own date, used when the log has nothing at all to say — the
     * case this rewrite exists for: the month must still appear, dated when it
     * was settled, badged Pending. `marked_paid_at` before `paid_at` because
     * `paid_at` can be backdated to the day the transfer actually landed,
     * while the attestation is when an admin recorded it.
     */
    const eventAt = pay.marked_paid_at ?? pay.paid_at ?? pay.due_date ?? null;
    const period = monthLabelDe(pay.due_date ?? pay.paid_at);
    rentCandidates.push({
      // The month, which "Mietzahlung eingegangen" never said: with one of
      // these per month on the card, an owner could not tell which period a
      // receipt was for, and a late month looked identical to a current one.
      desc: [`Mietzahlung${period ? ` ${period}` : ""} eingegangen`, who, unit]
        .filter(Boolean)
        .join(" · "),
      sent: !!attempts.sentAt,
      ts: attempts.sentAt ?? failed?.at ?? eventAt,
      reason: failed ? reasonText(failed.reason) : null,
      // Named only when an admin actually attested it: a Stripe or
      // tenant-side settlement has nobody behind it and must not be dressed
      // up as though it did.
      attribution: pay.marked_paid_by_id
        ? `als bezahlt markiert von ${pay.marked_paid_by_name?.trim() || "einem Admin"}`
        : null,
    });
  }

  /**
   * Log rows no payment claims: written before `payment_id` existed and left
   * unmatched by 20260904140000's backfill, or whose payment row is gone.
   * Rendered exactly as this card rendered every rent entry before that
   * migration — one entry per ATTEMPT — so no history disappears.
   *
   * Rent is the one event that cannot fall back on `failureFor`: it recurs,
   * so a failed September and a delivered October are two rows under the same
   * booking key and "did a later send supersede this" is the wrong question.
   * Each attempt stands on its own, which is also what keeps a single missed
   * month visible instead of masked by the months either side of it.
   */
  for (const log of rentLogs) {
    const pid = log.payment_id ?? null;
    if (pid && rentPaymentIds.has(pid)) continue;
    const b = bookingById.get(log.booking_id!);
    const who = b?.name?.trim() || "Tenant";
    const unit = b?.apartment_label ?? "—";
    const wasSent = isSentLog(log);
    rentCandidates.push({
      desc: `Mietzahlung eingegangen · ${who} · ${unit}`,
      sent: wasSent,
      ts: log.sent_at,
      reason: wasSent ? null : reasonText(log.skip_reason),
      // No payment to read an attestation off — that is what makes it legacy.
      attribution: null,
    });
  }

  rentCandidates
    .sort((a, b) => {
      const x = a.ts ?? "";
      const y = b.ts ?? "";
      return x < y ? 1 : x > y ? -1 : 0;
    })
    .slice(0, RENT_RECEIVED_LIMIT)
    .forEach((c) =>
      push(
        "Rent payment received",
        c.desc,
        c.sent,
        c.ts,
        c.reason,
        c.attribution
      )
    );

  // Reliable independently of owner_lifecycle_emails: stamped only after a
  // successful Resend call, never backfilled — see useOwnerEmailHistory.
  for (const inv of input.invoices) {
    if (!inv.sent_at) continue;
    push(
      "Monthly invoice sent",
      [inv.invoice_no, inv.period_month].filter(Boolean).join(" · ") ||
        "Management fee invoice",
      true,
      inv.sent_at
    );
  }

  for (const s of input.settlements) {
    const who = s.tenant_name?.trim() || "Tenant";
    if (s.bundle_sent_at) {
      push(
        "Deposit release requested",
        `Kaution freigeben · ${who}`,
        true,
        s.bundle_sent_at
      );
    }
    if (s.review_reminded_at) {
      push(
        "Deposit release reminder",
        `Erinnerung gesendet · ${who}`,
        true,
        s.review_reminded_at
      );
    }
    if (s.statement_sent_at) {
      push(
        "Deposit statement sent",
        `Kautionsabrechnung · ${who}`,
        true,
        s.statement_sent_at
      );
    }
    if (s.refund_paid_at) {
      push(
        "Deposit return confirmed",
        `Auszug & Kautionsabrechnung · ${who}`,
        true,
        s.refund_paid_at
      );
    }
  }

  // Anything with a real timestamp is a dated fact and sorts chronologically,
  // whether it was delivered, definitively was not, or is still pending — a
  // failed attempt happened at a moment, and so did a settled rent month
  // whose mail was never attempted. Burying either below every send would
  // hide the entries someone actually needs to act on. Only genuinely undated
  // entries fall to the bottom: "sent, timestamp unknown" first, then undated
  // failures, then the pending ones, which assert nothing at all.
  //
  // Pending-with-a-timestamp only became possible with the rent rewrite
  // above; every other pending entry passes `null` for `ts` by construction,
  // so their position at the bottom is unchanged.
  const t = (v: string) => new Date(v).getTime();
  const dated = out.filter((e) => !!e.ts).sort((a, b) => t(b.ts!) - t(a.ts!));
  const sentNoTs = out.filter((e) => !e.ts && e.sent);
  const undatedFailures = out.filter(
    (e) => !e.ts && !e.sent && e.notDeliveredReason
  );
  const pending = out.filter((e) => !e.ts && !e.sent && !e.notDeliveredReason);
  return [...dated, ...sentNoTs, ...undatedFailures, ...pending];
}
